import React, { type FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'
import { colors, Text } from 'ic-snacks'
import styled from '@emotion/styled'

import AppHeader from 'legacy/components/AppHeader'
import ContentContainer from 'legacy/components/ContentContainer'
import DocumentTitle from 'legacy/components/DocumentTitle'
import { type RetailerRouteComponentProps } from 'legacy/components/RetailerRoute'
import { type RetailerScopedRouteParams } from 'legacy/common/types'
import { trackEvent } from 'utils/events/trackEvent'
import { spacing } from 'gin-and-tonic/foundation/spacing'
import { useDomainAccessControl } from 'domains/insights-portal/utils/domain/accessControl'
import { insightsPortalAccessControl } from 'domains/insights-portal/access-control/insightsPortalAccess.configuration'
import { InsightsPortalSummary } from 'domains/insights-portal/components/summary/InsightsPortalSummary'
import FeatureEducationBanner from 'legacy/components/FeatureHighlights/FeatureEducationBanner'
import FeatureEducationModal from 'legacy/components/FeatureHighlights/FeatureEducationModal'
import useFeatureEducation from 'legacy/components/FeatureHighlights/useFeatureEducation'
import { useCurrentAccountDetails } from 'api/current-account/currentAccount.hooks'

export interface DashboardProps extends RetailerRouteComponentProps<RetailerScopedRouteParams> {}

const StyledFeatureEducationBanner = styled(FeatureEducationBanner)({
  featureEducation: {
    margin: spacing.X20,
  },
})

const Dashboard: FunctionComponent<DashboardProps> = ({
  match: {
    params: { partner_id, warehouse_id },
  },
}) => {
  const { account } = useCurrentAccountDetails()

  React.useEffect(() => {
    trackEvent({ id: 'dashboard.viewed', description: 'Viewed the dashboard page' })
  }, [])

  const hasInsightsAccess = useDomainAccessControl()(insightsPortalAccessControl)

  const { isOpen, onClose, onOpen, features, onDismissAll, setLastSeenFeatureId } =
    useFeatureEducation()

  return (
    <div>
      <FeatureEducationModal
        isOpen={isOpen}
        onClose={onClose}
        features={features}
        onNextFeature={id => setLastSeenFeatureId(id)}
      />
      <DocumentTitle titleId="dashboard.title" />
      <AppHeader title={<FormattedMessage id="dashboard.title" />} />
      <ContentContainer padding="20px">
        <div>
          <Text
            variant="T.36"
            fontWeight="light"
            style={{ color: colors.GRAY_46, marginTop: spacing.X20, marginBottom: spacing.X20 }}
          >
            <FormattedMessage
              id="dashboard.title.welcome"
              values={{
                name: account?.givenName,
              }}
            />
          </Text>
          {!!features.length && (
            <StyledFeatureEducationBanner
              onOpenFeatureEducationModal={onOpen}
              onDismiss={onDismissAll}
            />
          )}
          <InsightsPortalSummary hasInsightsAccess={hasInsightsAccess} />
        </div>
      </ContentContainer>
    </div>
  )
}

export default Dashboard
